import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  applySidebarStyle,
  applyTheme,
  getInitialTheme,
  resolveSidebarStyle,
  setStoredTheme,
  ThemeName,
} from 'waldur-design-tokens';

import { configuredSidebarStyle } from './sidebarStyleConfig';

interface ShellThemeValue {
  theme: ThemeName;
  toggleTheme: () => void;
}

const ShellThemeContext = createContext<ShellThemeValue | undefined>(undefined);

/**
 * Constructed once, inside AppShell, rather than by each app — the light/
 * dark toggle every micro-app's UserMenu wires up reads/writes the same
 * shared `waldur/theme/name` localStorage key and data-theme attribute the
 * main app's own src/theme/ThemeStorage.ts and src/theme/utils.ts's
 * applyTheme() use (so a theme choice made in either app carries over to the
 * other), and needs no app-specific input to do that — there was never a
 * reason for the app to compute this itself and hand it down as props.
 */
export function ShellThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<ThemeName>(getInitialTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggleTheme = () => {
    const next: ThemeName = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    setStoredTheme(next);
    // Only matters when SIDEBAR_STYLE is 'auto' (src/navigation/sidebar/
    // Sidebar.tsx's real "Match theme" option) — resolveSidebarStyle() is a
    // no-op passthrough for the other five, so this is safe to call
    // unconditionally rather than branching on configuredSidebarStyle here.
    applySidebarStyle(resolveSidebarStyle(configuredSidebarStyle, next));
  };

  return (
    <ShellThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ShellThemeContext.Provider>
  );
}

/** Must be called from inside <AppShell> — its Provider is what constructs the value. */
export function useShellTheme(): ShellThemeValue {
  const value = useContext(ShellThemeContext);
  if (!value) {
    throw new Error('useShellTheme() must be used inside <AppShell>.');
  }
  return value;
}
